<script setup lang="ts">
  /**
   * Textarea — 03-components/15b-textarea · OSCloud DS 1.1.34
   */
  import { useId } from 'vue'

  const props = withDefaults(
    defineProps<{
      modelValue?: string
      label?: string
      /** Sentence-case label variant for dense forms and consent copy. */
      plainLabel?: boolean
      placeholder?: string
      /** Neutral message under the control. */
      hint?: string
      /** Validation message; turns the message line red and marks the control invalid. */
      error?: string
      disabled?: boolean
      /** Turns the resize handle off. */
      fixed?: boolean
      fullWidth?: boolean
      rows?: number | string
      id?: string
    }>(),
    {
      modelValue: '',
      label: '',
      plainLabel: false,
      placeholder: '',
      hint: '',
      error: '',
      disabled: false,
      fixed: false,
      fullWidth: false,
      rows: undefined,
      id: undefined,
    },
  )

  defineEmits<{ 'update:modelValue': [value: string] }>()

  defineOptions({ inheritAttrs: false })

  const generatedId = useId()
  const controlId = props.id ?? generatedId
</script>

<template>
  <div class="form-field" :class="{ 'form-field--full-width': fullWidth }">
    <label
      v-if="label || $slots.label"
      :for="controlId"
      class="form-field__label"
      :class="{ 'form-field__label--plain': plainLabel }"
    >
      <slot name="label">{{ label }}</slot>
    </label>
    <div class="form-field__control">
      <textarea
        :id="controlId"
        v-bind="$attrs"
        class="form-field__textarea"
        :class="{ 'form-field__textarea--fixed': fixed }"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="rows"
        :aria-invalid="error ? 'true' : undefined"
        @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
      />
    </div>
    <span
      v-if="error || hint"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </div>
</template>

<style scoped lang="scss">
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--full-width {
      width: 100%;
    }

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__control {
      position: relative;
    }

    &__textarea {
      width: 100%;
      font-family: var(--bf-font-body);
      font-size: 15px;
      color: var(--bf-fg-1);
      background: var(--bf-form-control-surface);
      border: 1px solid var(--bf-form-control-hairline);
      border-radius: var(--bf-form-control-radius);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      /* The textarea's own contribution: min-height and resize. Wrapper sizes
         do not set a height on it. */
      min-height: 96px;
      padding: 12px 14px;
      line-height: var(--bf-lh-normal);
      resize: vertical;

      &--fixed {
        resize: none;
      }

      &::placeholder {
        color: var(--bf-form-control-placeholder);
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: var(--bf-form-control-hairline-hover);
      }

      &:focus-visible {
        outline: none;
        border-color: var(--bf-form-control-hairline-focus);
        box-shadow: var(--bf-form-focus-ring);
        background: var(--bf-form-control-surface-focus);
      }

      &:disabled {
        opacity: var(--bf-form-disabled-opacity);
        cursor: not-allowed;
      }

      &[aria-invalid='true'] {
        border-color: var(--bf-form-invalid-hairline);
        background: var(--bf-form-invalid-surface);
      }
    }

    &__message {
      /* The page body font is Work Sans; the message line is body copy, so it
         restates Source Sans 3 rather than inheriting. */
      font-family: var(--bf-font-body);
      color: var(--bf-fg-2);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }
</style>
